import React, { useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "utils/store";
import { toast } from "react-toastify";
import { strings } from "intl/fa";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import Header, { NavType } from "components/commons/header/Header";
import styles from "./Invoicing.module.scss";
import classNames from "classnames";
import Footer from "components/commons/footer/Footer";
import { Button, ButtonSize, TextField } from "@bale/ui";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";

import { digitsToHindi } from "@bale/utils";
import { useRequest } from "hooks/useRequest";
import { inquireShahkarThunk } from "core/inquireShahkar/shahkar.thunk";
import { getPanelsThunk, setAuthDataThunk } from "core/auth/auth.thunk";
import { NavigationActions, AppPage } from "core/navigation/navigation.slice";

const inputLengths = {
  nationalCode: 10,
  postalCode: 10,
};

export function PersonalInvoicing() {
  const [authorized, setAuthorized] = useState<boolean>(true);
  const dispatch = useAppDispatch();
  const navigationCallBack = useAppSelector(
    (state) => state.navigation.navigationCallBack
  );

  const myPanel = useAppSelector((state) => state.auth.myPanel);

  const { sendRequest: inquireShahkar } = useRequest(inquireShahkarThunk);
  const { sendRequest: getPanel } = useRequest(getPanelsThunk);
  const { sendRequest: setAuthData } = useRequest(setAuthDataThunk);

  const returnToOrdersList = useCallback(() => {
    dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
  }, []);

  const nationalCodeHandler = useInputHandler({
    numberOnly: true,
    rules: [GeneralValidators.required, GeneralValidators.minLength(10)],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.MinimumLength]:
        strings.invoicing.errors.national_code_length,
    },
  });
  const addressHandler = useInputHandler({
    rules: [GeneralValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
    },
  });
  const postalCodeHandler = useInputHandler({
    rules: [
      GeneralValidators.required,
      GeneralValidators.minLength(inputLengths.postalCode),
    ],
    numberOnly: true,
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.MinimumLength]:
        strings.invoicing.errors.postal_code_length,
    },
  });

  useEffect(() => {
    if (nationalCodeHandler.rawValue.length === 10) {
      inquireShahkar({
        phone: myPanel.phone,
        national_id: nationalCodeHandler.rawValue,
        onSuccess: ({ authorized }: any) => {
          if (authorized) {
            setAuthorized(true);
          } else {
            setAuthorized(false);
            nationalCodeHandler.setError(
              strings.invoicing.errors.national_phone_not_match
            );
          }
        },
      });
    }
  }, [nationalCodeHandler.rawValue]);

  const submitClickHandler = () => {
    if (!inputsAreValid) {
      toast.info(strings.common.error.all_required);
      return;
    }
    setAuthData({
      ...myPanel,
      national_code: nationalCodeHandler.rawValue,
      address: addressHandler.rawValue,
      zip_code: postalCodeHandler.rawValue,
    }).then(() => {
      getPanel().then(() => {
        navigationCallBack?.();
      });
    });
  };

  const inputsAreValid = Boolean(
    nationalCodeHandler.rawValue &&
      addressHandler.rawValue &&
      postalCodeHandler.rawValue &&
      authorized &&
      !postalCodeHandler.error &&
      !nationalCodeHandler.error
  );
  return (
    <>
      <Header
        onClose={returnToOrdersList}
        navType={NavType.both}
        title={strings.invoicing.invoicing}
      />
      <CardWrapper noScroll className={styles.Card}>
        <div className={styles.Form}>
          <div className={classNames(styles.HelpsWrapper, styles.FormItem)}>
            <div className={styles.HelpMain}>{strings.invoicing.help_main}</div>
            <div className={styles.HelpDesc}>
              {strings.invoicing.help_personal}
            </div>
          </div>
          <div className={styles.MobilePhoneContainer}>
            <div className={styles.MobileText}>
              {strings.invoicing.mobile_phone}
            </div>
            <div className={styles.MobileNumber}>
              {digitsToHindi(myPanel.phone)}
            </div>
          </div>
          <TextField
            {...nationalCodeHandler}
            label={strings.invoicing.national_code}
            placeholder="XXXXXXXXXX"
            helpText={strings.invoicing.errors.national_phone_should_match}
            dir="ltr"
            multiline={false}
            maxLength={inputLengths.nationalCode}
            error={
              nationalCodeHandler.error ||
              (authorized
                ? undefined
                : strings.invoicing.errors.national_phone_not_match)
            }
          />
          <TextField
            {...addressHandler}
            label={strings.invoicing.address}
            placeholder={strings.invoicing.address_help}
            dir="rtl"
            multiline={true}
          />
          <TextField
            {...postalCodeHandler}
            label={strings.invoicing.postal_code}
            placeholder="XXXXXXXXXX"
            dir="ltr"
            multiline={false}
            maxLength={inputLengths.postalCode}
            error={postalCodeHandler.error}
          />
          <div className={styles.FormItem}></div>
        </div>
      </CardWrapper>
      <Footer>
        <Button
          onClick={submitClickHandler}
          text={strings.common.button.confirm_and_submit}
          size={ButtonSize.XLarge}
          disableThemeOnly={!inputsAreValid}
        />
      </Footer>
    </>
  );
}
